import {
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import { useFeature, useToggleState, WebLink } from '@news-mono/web-common'
import React from 'react'
import { useLocation } from 'react-router'
import {
    StyledBackgroundWrap,
    StyledClickthrough,
    StyledClickthroughText,
    StyledCNBCLogo,
    StyledCommonwealthGamesContainer,
    StyledCommonwealthGamesText,
    StyledFeatureBorder,
    StyledFeatureContainer,
    StyledFeatureContainer__Deprecated,
    StyledFeatureHeaderContainer,
    StyledFeatureSectionHeader,
    StyledFeatureSectionHeaderContainer,
    StyledFeatureTitleContainer,
    StyledGradientSectionTitle,
    StyledGradientSectionTitleH1,
    StyledGradientWrap,
    StyledGrandFinalSectionHeader,
    StyledGrandFinalText,
    StyledGrandFinalWebLink,
    StyledIconArrowCircle,
    StyledPollieRaterBanner,
    StyledPollieRaterBannerButton,
    StyledPollieRaterBannerTitle,
    StyledPollieRaterBannerWrap,
    StyledThor,
    StyledTitleHeader,
} from '../../compositions/Thor/Thor.styled'
import { QueensLegacyLogo } from '../../logos/QueensLegacy/QueensLegacyLogo'
import { Breadcrumb, LinkProps } from '../../navigation'
import { ThemeOverrider } from '../../themes/ThemeOverrider/ThemeOverrider'
import { ContainerWidth, ThemeMargins } from '../../__styling/settings/metrics'
import { ThemeOverrideTypes } from '../../__styling/themes'

export interface ThorProps {
    mainContent: React.ReactElement<any>
    hasBackgroundFill?: boolean
    /** If true or undefined, will default to the outer margin of the theme */
    horizontalGutters?: boolean | keyof ThemeMargins
    /** If true or undefined, will default to the outer margin of the theme */
    verticalGutters?: boolean | keyof ThemeMargins
    verticalSpacing?: keyof ThemeMargins
    containerWidth?: ContainerWidth
    themeOverride?: ThemeOverrideTypes
    hideHeadingLink?: boolean
    // Pollie Rater / Federal Election specific props
    hidePollieRaterBanner?: boolean
    isHeadingH1?: boolean
    // {TODO} Move these from apps/thewest/src/app/routes/home/get-themed-segment.ts and into component-library
    layouts?: '4-card' | '8-card' | '7-card'
    hasBorder?: boolean
    // {TODO} Discuss if this should be retained
    unsetFeatureContainerBackground?: boolean
    hideFeatureHeader?: boolean
}

export const Thor: React.FC<ThorProps> = ({
    mainContent,
    hasBackgroundFill,
    horizontalGutters,
    verticalGutters,
    verticalSpacing,
    containerWidth,
    themeOverride,
    hideHeadingLink,
    hidePollieRaterBanner,
    isHeadingH1,
    layouts,
    hasBorder,
    unsetFeatureContainerBackground,
    hideFeatureHeader,
}) => {
    const styledThor = (
        <StyledThor
            hasBackgroundFill={hasBackgroundFill}
            horizontalGutters={horizontalGutters}
            verticalGutters={verticalGutters}
            verticalSpacing={verticalSpacing}
            containerWidth={containerWidth || 1020}
        >
            {mainContent}
        </StyledThor>
    )

    const location = useLocation()

    const toggles = useToggleState()

    const showPollieRaterBanner =
        useFeature('pollie-rater-drivers') && !hidePollieRaterBanner

    if (themeOverride === 'grand-final') {
        return (
            <ThemeOverrider override={themeOverride}>
                <StyledBackgroundWrap themeOverride={themeOverride}>
                    <StyledFeatureContainer__Deprecated
                        initialFlexDirection={'row'}
                        customPadding={16}
                        hasExtraBottomMargin={true}
                        layout={layouts}
                        unsetFeatureContainerBackground={
                            unsetFeatureContainerBackground
                        }
                    >
                        <StyledTitleHeader>
                            <StyledGrandFinalText>
                                AFL Finals 2022
                            </StyledGrandFinalText>
                        </StyledTitleHeader>
                        {!hideHeadingLink && (
                            <div>
                                <StyledGrandFinalWebLink to={'/sport/afl'}>
                                    <StyledGrandFinalSectionHeader>
                                        More Coverage
                                    </StyledGrandFinalSectionHeader>
                                    <StyledIconArrowCircle />
                                </StyledGrandFinalWebLink>
                            </div>
                        )}
                    </StyledFeatureContainer__Deprecated>
                    {styledThor}
                </StyledBackgroundWrap>
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'queens-legacy') {
        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer>
                    <WebLink to="/news/queen-elizabeth-ii">
                        <QueensLegacyLogo logoWidth={400} />
                    </WebLink>

                    <StyledFeatureSectionHeader
                        heading="More Stories"
                        headingUrl="/news/queen-elizabeth-ii"
                        showChevron={true}
                    />
                </StyledFeatureContainer>
                {styledThor}
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'cnbc') {
        //We don't want to display anything if we don't have any content.
        if (!mainContent) {
            return (
                <ThemeOverrider override={themeOverride}>
                    {styledThor}
                </ThemeOverrider>
            )
        }

        const CNBCLogoSource = require('../../logos/CNBCLogos/CNBC_Economist_Logo_White.png')

        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer__Deprecated>
                    <WebLink to="/business">
                        <StyledCNBCLogo src={CNBCLogoSource} />
                    </WebLink>
                    <StyledFeatureSectionHeader
                        heading="More stories"
                        headingUrl="/business"
                        showChevron
                    />
                </StyledFeatureContainer__Deprecated>
                {styledThor}
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'best-australian-yarn') {
        //We don't want to display anything if we don't have any content.
        if (!mainContent) {
            return (
                <ThemeOverrider override={themeOverride}>
                    {styledThor}
                </ThemeOverrider>
            )
        }

        const BestAustralianYarnLogoSource = isFeatureEnabled(
            toFeatureState(toggles),
            'BAY-top-14',
        )
            ? require('../../logos/BestAustralianYarn/CurationLogo_MinHeight40px_@2x.png')
            : require('../../logos/BestAustralianYarn/Logo Asset250w.png')

        const BestAustralianYarnTitle = isFeatureEnabled(
            toFeatureState(toggles),
            'BAY-top-14',
        )
            ? '2022 SHORT STORY WINNERS'
            : 'THE BEST AUSTRALIAN YARN 2022'

        return (
            <ThemeOverrider override={themeOverride}>
                <StyledFeatureContainer>
                    {!hideFeatureHeader && (
                        <StyledFeatureHeaderContainer>
                            <StyledFeatureTitleContainer>
                                <WebLink to="/lifestyle/best-australian-yarn">
                                    <img
                                        src={BestAustralianYarnLogoSource}
                                        height="40"
                                        alt="Top 50 Short Stories"
                                        style={{ display: 'block' }}
                                    />
                                </WebLink>
                                <h2>{BestAustralianYarnTitle}</h2>
                            </StyledFeatureTitleContainer>

                            <StyledClickthrough
                                to={'/lifestyle/best-australian-yarn'}
                            >
                                <StyledClickthroughText fontStyle="sans-serif">
                                    More Coverage
                                </StyledClickthroughText>
                                <StyledIconArrowCircle />
                            </StyledClickthrough>
                        </StyledFeatureHeaderContainer>
                    )}

                    {styledThor}
                </StyledFeatureContainer>
            </ThemeOverrider>
        )
    }

    if (themeOverride === 'commonwealth-games') {
        if (layouts === '4-card') {
            return (
                <ThemeOverrider override={themeOverride}>
                    <StyledFeatureContainer__Deprecated
                        hasExtraBottomMargin={true}
                    >
                        <StyledCommonwealthGamesContainer>
                            <StyledCommonwealthGamesText>
                                COMMONWEALTH &nbsp;
                                <br />
                                GAMES 2022
                            </StyledCommonwealthGamesText>
                        </StyledCommonwealthGamesContainer>
                        <StyledFeatureSectionHeaderContainer>
                            <StyledFeatureSectionHeader
                                heading="Full Coverage"
                                headingUrl="/sport/commonwealth-games"
                                showChevron
                                colorOverride={'#fff'}
                            />
                        </StyledFeatureSectionHeaderContainer>
                    </StyledFeatureContainer__Deprecated>
                    {styledThor}
                </ThemeOverrider>
            )
        } else if (layouts === '7-card') {
            const commonwealthGamesHeaders: LinkProps[] = [
                {
                    text: 'Sport',
                    href: '/sport',
                },
                {
                    text: 'Commonwealth Games',
                    href: '/sport/commonwealth-games',
                },
            ]

            const headingText =
                location.pathname === '/sport' ? (
                    <StyledGradientSectionTitle>
                        Commonwealth Games
                    </StyledGradientSectionTitle>
                ) : (
                    <>
                        <Breadcrumb
                            items={commonwealthGamesHeaders}
                            enableHeaderTag={true}
                            verticalSpacing={'md'}
                        />
                    </>
                )

            return (
                <React.Fragment>
                    <StyledGradientWrap
                        linearGradientColors={['#021487', '#6E259B', '#BD37AC']}
                    >
                        <StyledFeatureContainer__Deprecated>
                            {headingText}
                            {!hideHeadingLink && (
                                <StyledFeatureSectionHeader
                                    heading="Full Coverage"
                                    headingUrl="/sport/commonwealth-games"
                                    showChevron
                                    colorOverride={'#fff'}
                                />
                            )}
                        </StyledFeatureContainer__Deprecated>
                        {styledThor}
                    </StyledGradientWrap>
                    {hasBorder && <StyledFeatureBorder />}
                </React.Fragment>
            )
        }
    }

    if (themeOverride === 'federal-election') {
        return (
            <StyledGradientWrap
                linearGradientColors={['#021487', '#6E259B', '#BD37AC']}
                boxShadow={true}
            >
                <StyledFeatureContainer__Deprecated>
                    {isHeadingH1 ? (
                        <StyledGradientSectionTitleH1>
                            Federal Election 2022
                        </StyledGradientSectionTitleH1>
                    ) : (
                        <StyledGradientSectionTitle>
                            Federal Election 2022
                        </StyledGradientSectionTitle>
                    )}

                    {!hideHeadingLink && (
                        <StyledFeatureSectionHeader
                            heading="Full Coverage"
                            headingUrl="/politics/federal-election"
                            showChevron={true}
                        />
                    )}
                </StyledFeatureContainer__Deprecated>
                {showPollieRaterBanner && (
                    <StyledPollieRaterBannerWrap>
                        <StyledPollieRaterBanner>
                            <StyledPollieRaterBannerTitle>
                                Rate the politicians running in the election!
                            </StyledPollieRaterBannerTitle>
                            <StyledPollieRaterBannerButton to="/politics/federal-election/pollie-rater">
                                Rate the politicians
                            </StyledPollieRaterBannerButton>
                        </StyledPollieRaterBanner>
                    </StyledPollieRaterBannerWrap>
                )}
                {styledThor}
            </StyledGradientWrap>
        )
    }

    return (
        <ThemeOverrider override={themeOverride}>{styledThor}</ThemeOverrider>
    )
}

Thor.displayName = 'ThorComposition'
